import { CheckCircleFilled, ClockCircleOutlined } from "@ant-design/icons";
import { Button, DatePicker, Input, Select, Skeleton } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCreatePublicAppointment } from "../../api/api.post";
import {
  useGetBookableServices,
  useGetSchedulingAvailability,
  useGetSchedulingProviders,
  useGetStoreDetailsForOnlineOrder,
} from "../../api/api.get";
import EmptyComponent from "../../components/empty.component";
import { useNotifications2 } from "../../components/notifications/notification";
import { OrgDataComponent } from "../../components/org.data.component";
import { useStoreContext } from "../../providers/store.provider";
import { IBookableService, IPublicSlot } from "../../types/types";
import { toFixedDisplay } from "../../utils/number.utils";
import { capitalize } from "../../utils/functions";

const ANY_PROVIDER = "any";

export default function AppointmentsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currency } = useStoreContext();
  const { notifyError, notification } = useNotifications2();

  const { data: storeDetailsData, error } =
    useGetStoreDetailsForOnlineOrder(id);
  const { data: services, isLoading: servicesLoading } =
    useGetBookableServices(id);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [provider, setProvider] = useState<string>(ANY_PROVIDER);
  const [date, setDate] = useState<Dayjs>();
  const [slot, setSlot] = useState<IPublicSlot>();

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

  const [confirmation, setConfirmation] = useState<{
    code: string;
    startTime: string;
  }>();

  const { data: providers } = useGetSchedulingProviders(id, selectedIds);
  const employeeId = provider === ANY_PROVIDER ? undefined : +provider;

  const { data: slots, isFetching: slotsLoading } =
    useGetSchedulingAvailability(id, {
      date: date?.format("YYYY-MM-DD"),
      productIds: selectedIds,
      employeeId,
      tzOffsetMinutes: new Date().getTimezoneOffset(),
    });

  const { mutateAsync: book, isPending: booking } =
    useCreatePublicAppointment(id);

  // 404 → not found, mirroring the online-order page.
  useEffect(() => {
    if ((error as any)?.status === 404) navigate("/not-found");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  // Changing services or provider invalidates the chosen slot.
  useEffect(() => setSlot(undefined), [selectedIds, provider, date]);

  const bg = storeDetailsData?.bodyBackgroundColor ?? "white";
  const fg = storeDetailsData?.bodyFontColor ?? "black";

  const selectedServices = useMemo(
    () => (services ?? []).filter((s) => selectedIds.includes(s._id)),
    [services, selectedIds],
  );
  const subtotal = useMemo(
    () => selectedServices.reduce((sum, s) => sum + (s.price?.value ?? 0), 0),
    [selectedServices],
  );
  const totalDuration = useMemo(
    () =>
      selectedServices.reduce((sum, s) => sum + (s.durationMinutes ?? 0), 0),
    [selectedServices],
  );

  const toggleService = (s: IBookableService) =>
    setSelectedIds((prev) =>
      prev.includes(s._id) ? prev.filter((x) => x !== s._id) : [...prev, s._id],
    );

  const canBook =
    selectedIds.length > 0 && !!slot && customerName.trim().length > 0;

  const handleBook = () => {
    if (!slot) return;
    book({
      productIds: selectedIds,
      employeeId: slot.employeeId,
      startTime: slot.startTime,
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim() || undefined,
      customerEmail: customerEmail.trim() || undefined,
    })
      .then((res) => {
        setConfirmation({
          code: res.confirmationCode,
          startTime: slot.startTime,
        });
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ?? "Could not book. Try again.";
        notifyError(Array.isArray(msg) ? msg[0] : msg);
      });
  };

  const resetForAnother = () => {
    setConfirmation(undefined);
    setSelectedIds([]);
    setProvider(ANY_PROVIDER);
    setDate(undefined);
    setSlot(undefined);
    setCustomerName("");
    setCustomerPhone("");
    setCustomerEmail("");
  };

  const providerOptions = [
    { value: ANY_PROVIDER, label: "Any available" },
    ...(providers ?? []).map((p) => ({ value: String(p.id), label: p.name })),
  ];

  return (
    <div
      className="mx-auto max-w-3xl min-h-screen"
      style={{ background: bg, color: fg, paddingBottom: "3rem" }}
    >
      {notification}

      <div
        className="py-10 px-4"
        style={{
          background: storeDetailsData?.headerBackgroundColor ?? "white",
          color: storeDetailsData?.headerFontColor ?? "black",
        }}
      >
        <OrgDataComponent storeDetailsData={storeDetailsData} variant="bold" />
        <h1 className="text-2xl font-bold mt-4">Book an Appointment</h1>
      </div>

      {confirmation ? (
        <div className="p-6 flex flex-col items-center text-center gap-3 my-10">
          <CheckCircleFilled style={{ fontSize: 56, color: "#16a34a" }} />
          <h2 className="text-2xl font-bold">You're booked!</h2>
          <p className="text-lg">
            {dayjs(confirmation.startTime).format("ddd, DD MMM YYYY · h:mm A")}
          </p>
          <p>
            Confirmation code:{" "}
            <strong className="tracking-widest">{confirmation.code}</strong>
          </p>
          <p className="opacity-70 max-w-md">
            Please keep this code. The business will confirm your appointment
            shortly.
          </p>
          <Button className="mt-4" onClick={resetForAnother}>
            Book another
          </Button>
        </div>
      ) : (
        <div className="p-4 flex flex-col gap-8">
          {/* 1. Services */}
          <section>
            <h3 className="text-lg font-bold mb-3">1. Choose service(s)</h3>
            <Skeleton active loading={servicesLoading}>
              {(services ?? []).length === 0 ? (
                <EmptyComponent color={fg} title="No services available yet." />
              ) : (
                <div
                  className="flex flex-col gap-3 overflow-y-auto pr-1"
                  style={{ maxHeight: "22rem" }}
                >
                  {(services ?? []).map((s) => {
                    const selected = selectedIds.includes(s._id);
                    return (
                      <button
                        key={s._id}
                        onClick={() => toggleService(s)}
                        className="flex items-center justify-between gap-3 rounded-xl border p-4 text-left"
                        style={{
                          borderColor: selected ? "#2563eb" : "#d1d5db",
                          borderWidth: selected ? 2 : 1,
                        }}
                      >
                        <div className="flex flex-col">
                          <span className="font-semibold">
                            {capitalize(s.title)}
                          </span>
                          {!!s.durationMinutes && (
                            <span className="text-sm opacity-70">
                              <ClockCircleOutlined /> {s.durationMinutes} min
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-bold">
                            {currency}
                            {toFixedDisplay(s.price?.value ?? 0)}
                          </span>
                          {selected && (
                            <CheckCircleFilled style={{ color: "#2563eb" }} />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </Skeleton>
          </section>

          {selectedIds.length > 0 && (
            <>
              {/* 2. Provider */}
              <section>
                <h3 className="text-lg font-bold mb-3">2. Choose a provider</h3>
                <Select
                  size="large"
                  className="w-full"
                  value={provider}
                  onChange={setProvider}
                  options={providerOptions}
                />
              </section>

              {/* 3. Date */}
              <section>
                <h3 className="text-lg font-bold mb-3">3. Pick a date</h3>
                <DatePicker
                  size="large"
                  className="w-full"
                  value={date}
                  onChange={(d) => setDate(d ?? undefined)}
                  disabledDate={(d) => d && d.isBefore(dayjs().startOf("day"))}
                  format="DD MMM YYYY"
                />
              </section>

              {/* 4. Time */}
              {date && (
                <section>
                  <h3 className="text-lg font-bold mb-3">4. Pick a time</h3>
                  <Skeleton active loading={slotsLoading}>
                    {(slots ?? []).length === 0 ? (
                      <EmptyComponent
                        color={fg}
                        title="No open times for this day. Try another date or provider."
                      />
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(slots ?? []).map((sl) => {
                          const active = slot?.startTime === sl.startTime;
                          return (
                            <button
                              key={sl.startTime}
                              onClick={() => setSlot(sl)}
                              className="rounded-full border px-4 py-2"
                              style={{
                                borderColor: active ? "#2563eb" : "#d1d5db",
                                background: active ? "#2563eb" : "transparent",
                                color: active ? "white" : fg,
                              }}
                            >
                              {dayjs(sl.startTime).format("h:mm A")}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </Skeleton>
                  {slot && provider === ANY_PROVIDER && (
                    <p className="text-sm opacity-70 mt-2">
                      With {slot.employeeName}
                    </p>
                  )}
                </section>
              )}

              {/* 5. Details */}
              {slot && (
                <section className="flex flex-col gap-3">
                  <h3 className="text-lg font-bold">5. Your details</h3>
                  <Input
                    size="large"
                    placeholder="Full name *"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                  />
                  <Input
                    size="large"
                    placeholder="Phone"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                  />
                  <Input
                    size="large"
                    placeholder="Email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                  />
                </section>
              )}

              {/* Summary + submit */}
              <div className="rounded-xl border p-4 flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="font-semibold">
                    {selectedServices.length} service
                    {selectedServices.length > 1 ? "s" : ""}
                    {totalDuration > 0 ? ` · ${totalDuration} min` : ""}
                  </span>
                  <span className="text-lg font-bold">
                    {currency}
                    {toFixedDisplay(subtotal)}
                  </span>
                </div>
                <Button
                  type="primary"
                  size="large"
                  loading={booking}
                  disabled={!canBook}
                  onClick={handleBook}
                >
                  Book appointment
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
